import authStorage from "src/utils/authStorage";
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { AuthContext } from './AuthProvider';

import { useAttendantChannel } from './WebSocketProvider';
import api from '../utils/api';

// Context for attendant state management
const AttendantContext = createContext();

// Custom hook to use attendant context
export const useAttendant = () => {
  const context = useContext(AttendantContext);
  if (!context) {
    throw new Error('useAttendant must be used within AttendantProvider');
  }
  return context;
};

// Safe version of the hook that returns null if provider is not available
export const useAttendantSafe = () => {
  try {
    const context = useContext(AttendantContext);
    return context;
  } catch {
    return null;
  }
};

/**
 * AttendantProvider
 * Manages global state for attendant functionality
 *
 * Features:
 * - Tracks pending chats count
 * - Real-time updates via WebSocket
 * - API polling as fallback
 * - Shared state across components
 */
export const AttendantProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  // NOTE: pendingCount/activeChats are NOT filtered by assistant — attendant sees everything
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingChats, setPendingChats] = useState([]);
  const [activeChats, setActiveChats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentAttendantId, setCurrentAttendantId] = useState(null);

  // Refs for cleanup
  const isMountedRef = useRef(true);
  const attendantIdRef = useRef(null);

  // Check if user has permission to view attendant data
  const hasPermission = useCallback(() => {
    if (!user) return false;

    // Admin users don't have attendant functionality
    if (user.userType === 'admin') return false;

    // Sales users don't have attendant functionality
    if (user.userType === 'sales') return false;

    // Partner without userGroup has full access
    if (!user.userGroupId) return true;

    // Check for full access flag
    if (user.userGroup?.fullAccess) return true;

    // Check specific permissions
    const permissions = user.userGroup?.roles || user.userGroup?.permissions || [];
    return permissions.some(p =>
      ['READ_ATTENDANT_PENDING_CHATS', 'READ_ATTENDANT_ACTIVE_CHATS', 'READ_ATTENDANT'].includes(p)
    );
  }, [user]);

  // Fetch the current attendant ID for this partner
  const fetchCurrentAttendant = useCallback(async () => {
    if (!hasPermission()) {
      setCurrentAttendantId(null);
      return null;
    }

    try {
      // Use the status endpoint which returns current attendant info
      const response = await api.get('/partners/attendant/status');
      const attendantData = response.data?.data;
      const attendantId = attendantData?.id || attendantData?.attributes?.id;

      if (isMountedRef.current && attendantId) {
        console.log('AttendantProvider: Found attendant ID:', attendantId);
        setCurrentAttendantId(attendantId);
        attendantIdRef.current = attendantId;
        return attendantId;
      }

      console.warn('AttendantProvider: No attendant found for this partner');
      return null;
    } catch (error) {
      console.error('Error fetching current attendant:', error);
      return null;
    }
  }, [hasPermission]);

  // Fetch pending chats from API
  const fetchPendingChats = useCallback(async () => {
    // Only fetch if user has permission
    if (!hasPermission()) {
      setPendingCount(0);
      setPendingChats([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get('/partners/attendant/pending_chats');

      if (isMountedRef.current) {
        const chats = response.data?.data || [];
        setPendingChats(Array.isArray(chats) ? chats : []);
        setPendingCount(Array.isArray(chats) ? chats.length : 0);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching pending chats:', error);
      if (isMountedRef.current) {
        setError(error.message);
        // Don't reset count on error - keep last known value
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [hasPermission]);

  // Fetch active chats from API
  const fetchActiveChats = useCallback(async () => {
    // Only fetch if user has permission
    if (!hasPermission()) {
      setActiveChats([]);
      return;
    }

    try {
      const response = await api.get('/partners/attendant/active_chats');

      if (isMountedRef.current) {
        const chats = response.data?.data || response.data || [];
        setActiveChats(Array.isArray(chats) ? chats : []);
      }
    } catch (error) {
      console.error('Error fetching active chats:', error);
    }
  }, [hasPermission]);

  // WebSocket handlers
  const wsHandlers = useMemo(() => ({
    onConnected: () => {
      console.log('AttendantProvider: WebSocket connected');
      // Fetch initial data
      fetchPendingChats();
      fetchActiveChats();
    },

    onDisconnected: () => {
      console.log('AttendantProvider: WebSocket disconnected');
    },

    onNewTakeoverRequest: (takeoverId, data) => {
      console.log('AttendantProvider: New takeover request received', takeoverId);

      // Fetch the takeover data and add to pending chats
      fetchPendingChats();

      // Update counter immediately
      setPendingCount(prev => {
        const newCount = prev + 1;
        console.log('AttendantProvider: Updated pending count:', newCount);
        return newCount;
      });
      setLastUpdate(new Date());

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('new_takeover_request', {
        detail: { takeover_id: takeoverId, ...data }
      }));
    },

    onTakeoverReceived: (takeover) => {
      console.log('AttendantProvider: New takeover received', takeover);

      // Add to pending chats
      setPendingChats(prev => {
        // Avoid duplicates
        if (prev.find(c => c.id === takeover.id)) return prev;
        const newList = [...prev, takeover];
        console.log('AttendantProvider: Updated pending chats count:', newList.length);
        return newList;
      });

      // Update counter based on new list
      setPendingCount(prev => {
        const newCount = prev + 1;
        console.log('AttendantProvider: Updated pending count:', newCount);
        return newCount;
      });
      setLastUpdate(new Date());
    },

    onTakeoverAccepted: (data) => {
      console.log('AttendantProvider: Takeover accepted', data);

      // Remove from pending chats
      setPendingChats(prev => {
        const newList = prev.filter(c => c.id !== data.takeover_id);
        console.log('AttendantProvider: Pending chats after accept:', newList.length);
        return newList;
      });

      // If accepted by this attendant, add to active chats
      if (data.attendant_id === attendantIdRef.current) {
        const acceptedChat = pendingChats.find(c => c.id === data.takeover_id);
        if (acceptedChat) {
          setActiveChats(prev => {
            const newList = [...prev, { ...acceptedChat, status: 'active' }];
            console.log('AttendantProvider: Active chats after accept:', newList.length);
            return newList;
          });
        }
      }

      // Update counter
      setPendingCount(prev => {
        const newCount = Math.max(0, prev - 1);
        console.log('AttendantProvider: Updated pending count after accept:', newCount);
        return newCount;
      });
      setLastUpdate(new Date());

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('takeover_accepted', { detail: data }));
    },

    onTakeoverCompleted: (data) => {
      console.log('AttendantProvider: Takeover completed', data);

      // Remove from active chats
      setActiveChats(prev => {
        const newList = prev.filter(c => c.id !== data.takeover_id);
        console.log('AttendantProvider: Active chats after complete:', newList.length);
        return newList;
      });

      // Remove from pending if it was there
      setPendingChats(prev => prev.filter(c => c.id !== data.takeover_id));

      // Refresh the counts
      fetchPendingChats();
      fetchActiveChats();

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('takeover_completed', { detail: data }));
    },

    onTakeoverRejected: (data) => {
      console.log('AttendantProvider: Takeover rejected', data);

      // Remove from pending chats
      setPendingChats(prev => {
        const newList = prev.filter(c => c.id !== data.takeover_id);
        console.log('AttendantProvider: Pending chats after reject:', newList.length);
        return newList;
      });

      // Update counter
      setPendingCount(prev => {
        const newCount = Math.max(0, prev - 1);
        console.log('AttendantProvider: Updated pending count after reject:', newCount);
        return newCount;
      });
      setLastUpdate(new Date());
      window.dispatchEvent(new CustomEvent('takeover_rejected', { detail: data }));
    },

    // WaitingTakeoverTimeoutJob devolveu a conversa à IA por falta de atendente
    onTakeoverTimeoutReturned: (data) => {
      setPendingChats(prev => prev.filter(c => c.id !== data.takeover_id));
      setPendingCount(prev => Math.max(0, prev - 1));
      setLastUpdate(new Date());
      window.dispatchEvent(new CustomEvent('takeover_timeout_returned', { detail: data }));
    },

    onTakeoverReturnedToBot: (data) => {
      console.log('AttendantProvider: Takeover returned to bot', data);

      // Refresh chats
      fetchPendingChats();
      fetchActiveChats();

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('takeover_returned_to_bot', { detail: data }));
    },

    onMessageReceived: (data) => {
      // Just log for now, individual components can handle message display
      console.log('AttendantProvider: Message received for takeover', data?.takeover_id);
    },

    onNewClientMessage: (data) => {
      const { takeover_id: takeoverId, attendant_id: attendantId, message } = data || {};
      console.log('AttendantProvider: New client message for takeover', takeoverId, message);

      // Payload completo (inclui assistant_id) + campos derivados que as telas legadas leem
      const event = new CustomEvent('attendant_message_received', {
        detail: {
          ...data,
          takeover_id: takeoverId,
          attendant_id: attendantId,
          message: message,
          sender: message?.sender || 'client',
          content: message?.content || message?.message || '',
          created_at: message?.created_at || new Date().toISOString()
        }
      });

      console.log('AttendantProvider: Dispatching event with detail:', event.detail);
      window.dispatchEvent(event);
      console.log('AttendantProvider: Event dispatched successfully');
    },

    // Novos handlers para chats do bot
    onNewBotChat: (data) => {
      console.log('AttendantProvider: New bot chat', data);
      fetchPendingChats();
      fetchActiveChats();

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('new_bot_chat', { detail: data }));
    },

    onChatsUpdated: (data) => {
      console.log('AttendantProvider: Chats updated', data);
      fetchPendingChats();
      fetchActiveChats();

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('chats_updated', { detail: data }));
    },

    onChatStatusChanged: (data) => {
      console.log('AttendantProvider: Chat status changed', data);
      fetchPendingChats();
      fetchActiveChats();

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('chat_status_changed', { detail: data }));
    },

    // Typing indicator (Instagram and other channels)
    onTypingIndicator: (data) => {
      console.log('AttendantProvider: Typing indicator', data);

      // Dispatch event for components to update UI
      window.dispatchEvent(new CustomEvent('typing_indicator', { detail: data }));
    },

    // Instagram 24h window expiring alert
    onWindowExpiringAlert: (data) => {
      console.log('AttendantProvider: Instagram window expiring alert', data);

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('window_expiring_alert', { detail: data }));
    },

    // New message during active takeover
    onNewMessage: (data) => {
      console.log('AttendantProvider: New message during takeover', data);

      // Dispatch event for components
      window.dispatchEvent(new CustomEvent('attendant_new_message', { detail: data }));
    },

    onError: (error) => {
      console.error('AttendantProvider: WebSocket error', error);
    }
  }), [fetchPendingChats, fetchActiveChats, pendingChats]);

  // Setup WebSocket connection if user has permission
  const partnerId = user?.partner_id || user?.id;
  const token = typeof window !== 'undefined' ? authStorage.getItem('@sacgpt:token') : null;

  // Use currentAttendantId (fetched from API) instead of user.id
  const shouldConnect = hasPermission() && partnerId && currentAttendantId && token;

  // Use WebSocket hook conditionally
  const {
    isConnected,
    acceptTakeover: wsAcceptTakeover,
    sendAttendantMessage
  } = useAttendantChannel(
    shouldConnect ? partnerId : null,
    shouldConnect ? currentAttendantId : null,
    shouldConnect ? token : null,
    shouldConnect ? wsHandlers : {}
  );

  // Initial fetch - first get attendant ID, then fetch chats
  useEffect(() => {
    if (!hasPermission()) {
      setPendingCount(0);
      setPendingChats([]);
      setActiveChats([]);
      setCurrentAttendantId(null);
      return;
    }

    // Fetch current attendant ID first, then chats
    const initializeAttendant = async () => {
      await fetchCurrentAttendant();
      fetchPendingChats();
      fetchActiveChats();
    };

    initializeAttendant();
  }, [fetchCurrentAttendant, fetchPendingChats, fetchActiveChats, hasPermission]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update attendantIdRef when currentAttendantId changes
  useEffect(() => {
    attendantIdRef.current = currentAttendantId;
  }, [currentAttendantId]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchPendingChats();
    fetchActiveChats();
  }, [fetchPendingChats, fetchActiveChats]);

  // Accept takeover function
  const acceptTakeover = useCallback(async (takeoverId) => {
    let takeover = null;

    try {
      console.log('AttendantProvider: Accepting takeover', takeoverId);

      // Optimistically move from pending to active
      takeover = pendingChats.find(c => c.id === takeoverId);
      if (takeover) {
        setPendingChats(prev => prev.filter(c => c.id !== takeoverId));
        setActiveChats(prev => [...prev, { ...takeover, status: 'active' }]);
        setPendingCount(prev => Math.max(0, prev - 1));
      }

      // Send via WebSocket
      await wsAcceptTakeover(takeoverId);

      return true;
    } catch (error) {
      console.error('AttendantProvider: Error accepting takeover', error);

      // Revert optimistic update
      if (takeover) {
        setPendingChats(prev => [...prev, takeover]);
        setActiveChats(prev => prev.filter(c => c.id !== takeoverId));
        setPendingCount(prev => prev + 1);
      }

      throw error;
    }
  }, [pendingChats, wsAcceptTakeover]);

  // Complete takeover function
  const completeTakeover = useCallback(async (takeoverId) => {
    try {
      console.log('AttendantProvider: Completing takeover', takeoverId);

      // Call API to complete
      await api.post(`/partners/attendant/chats/${takeoverId}/complete`);

      // Remove from active chats
      setActiveChats(prev => prev.filter(c => c.id !== takeoverId));

      return true;
    } catch (error) {
      console.error('AttendantProvider: Error completing takeover', error);
      throw error;
    }
  }, []);

  // Reject takeover function
  const rejectTakeover = useCallback(async (takeoverId) => {
    let takeover = null;

    try {
      console.log('AttendantProvider: Rejecting takeover', takeoverId);

      // Optimistically remove from pending chats
      takeover = pendingChats.find(c => c.id === takeoverId);
      if (takeover) {
        setPendingChats(prev => prev.filter(c => c.id !== takeoverId));
        setPendingCount(prev => Math.max(0, prev - 1));
      }

      // Call API to reject
      await api.post(`/partners/attendant/chats/${takeoverId}/reject`);

      return true;
    } catch (error) {
      console.error('AttendantProvider: Error rejecting takeover', error);

      // Revert optimistic update
      if (takeover) {
        setPendingChats(prev => [...prev, takeover]);
        setPendingCount(prev => prev + 1);
      }

      throw error;
    }
  }, [pendingChats]);

  // Context value
  const value = {
    // State
    pendingCount,
    pendingChats,
    activeChats,
    isLoading,
    error,
    lastUpdate,
    isConnected,
    currentAttendantId,

    // Methods
    refresh,
    acceptTakeover,
    completeTakeover,
    rejectTakeover,
    sendAttendantMessage,

    // Permissions
    hasPermission: hasPermission()
  };

  return (
    <AttendantContext.Provider value={value}>
      {children}
    </AttendantContext.Provider>
  );
};

export default AttendantProvider;